import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { authService } from '../../services/authService';
import { getBackendHost } from '../../config/EnvData';
import AuthForm from './AuthForm';
import { CircularProgress, Box } from '@mui/material';
import styled from 'styled-components';

// Fire-and-forget access ping. The backend records the authenticated user (from the verified
// Cognito token) in the ra-user-access table so the admin console can report who signed up and
// actually used the app. Best-effort: any failure is swallowed so it never blocks the app.
const trackAppAccess = async () => {
  try {
    const token = await authService.getIdToken();
    if (!token) return;
    await axios.post(`${getBackendHost()}/api/track-access/`, {},
      { headers: { Authorization: `Bearer ${token}` } });
  } catch (error) {
    console.debug('access tracking skipped:', error?.message);
  }
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #275AA6 0%, #1e4a8c 100%);
`;

const ProtectedRoute = ({ children,user,setUser}) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const result = await authService.getCurrentUserWithAttributes();
      if (result.success) {
        console.log('User authenticated with attributes:', result.user);
        setUser(result.user);
        setIsAuthenticated(true);
        trackAppAccess();
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.log('User not authenticated:', error.message);
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const handleAuthSuccess = async (authenticatedUser) => {
    console.log('Authentication successful, fetching user attributes...');
    try {
      // Fetch user with attributes after successful authentication
      const result = await authService.getCurrentUserWithAttributes();
      if (result.success) {
        console.log('User data fetched after sign-in:', result.user);
        setUser(result.user);
        setIsAuthenticated(true);
      } else {
        // Fallback to basic user data
        setUser(authenticatedUser);
        setIsAuthenticated(true);
      }
      trackAppAccess();
    } catch (error) {
      console.warn('Failed to fetch user attributes after sign-in:', error);
      // Still proceed with basic user data
      setUser(authenticatedUser);
      setIsAuthenticated(true);
    }

  };

  if (loading) {
    return (
      <LoadingContainer>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={50} sx={{ color: 'white' }} />
          <Box color="white" fontSize="16px">Loading...</Box>
        </Box>
      </LoadingContainer>
    );
  }

  if (!isAuthenticated) {
    return <AuthForm onAuthSuccess={handleAuthSuccess} />;
  }

  return React.cloneElement(children, { user });
};

export default ProtectedRoute;